// One Invoice Log row: its fields and lines, Exclude (with a reason), Mark
// Duplicate, Restore, notes, and Re-extract with a field-by-field diff before
// anything is saved.

// REACT IMPORTS
import React, { useEffect, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import {
	Alert, Button, CircularProgress, Collapse, Dialog, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, TextField,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";

// API, CONSTANTS
import { getAPICall, patchAPICall, postAPICall } from "../../../config/apiCalls";
import { STATUS_PILLS, apiError, fmtDateTime, fmtDay, fmtUSD, pillStyle } from "../itBudgetUtils";

const Field = ({ label, children }) => (
	<div>
		<div className="itBudgetOverline">{label}</div>
		<div style={{ fontSize: 14 }}>{children ?? "—"}</div>
	</div>
);

const InvoiceDialog = ({ open, invoiceId, canEdit, onClose, onSaved }) => {
	const { instance, accounts } = useMsal();
	const [invoice, setInvoice] = useState(null);
	const [loading, setLoading] = useState(false);
	const [busy, setBusy] = useState("");
	const [error, setError] = useState("");
	const [reason, setReason] = useState("");
	const [notes, setNotes] = useState("");
	const [reread, setReread] = useState(null);

	useEffect(() => {
		if (!open || !invoiceId) return;
		setInvoice(null);
		setReread(null);
		setReason("");
		setError("");
		setLoading(true);
		getAPICall(instance, accounts[0], `/api/it-budget/invoices/${invoiceId}`)
			.then((res) => {
				setInvoice(res.data);
				setNotes(res.data.notes || "");
			})
			.catch((err) => setError(apiError(err, "Couldn't load the invoice.")))
			.finally(() => setLoading(false));
	}, [open, invoiceId]);

	const patch = async (body, message) => {
		setBusy(body.action || "save");
		setError("");
		try {
			const res = await patchAPICall(instance, accounts[0], `/api/it-budget/invoices/${invoiceId}`, { ...body, expectedModified: invoice.modified });
			setInvoice(res.data.invoice);
			onSaved(message);
		} catch (err) {
			setError(apiError(err, "Couldn't save."));
		} finally {
			setBusy("");
		}
	};

	const reExtract = async () => {
		setBusy("reextract");
		setError("");
		try {
			const res = await postAPICall(instance, accounts[0], `/api/it-budget/invoices/${invoiceId}/re-extract`, {});
			setReread(res.data);
		} catch (err) {
			setError(apiError(err, "Re-extract failed."));
		} finally {
			setBusy("");
		}
	};

	const applyReExtract = async () => {
		setBusy("apply");
		try {
			const res = await postAPICall(instance, accounts[0], `/api/it-budget/invoices/${invoiceId}/re-extract/apply`, {
				prompt_id: reread.prompt.id,
				extract: reread.extract,
				review_reasons: reread.review_reasons,
			});
			setInvoice(res.data.invoice);
			setReread(null);
			onSaved(`Saved the values read by ${reread.prompt.name} v${reread.prompt.version}.`);
		} catch (err) {
			setError(apiError(err, "Couldn't save the new values."));
		} finally {
			setBusy("");
		}
	};

	const excluded = invoice && ["excluded", "duplicate"].includes(invoice.status_name);

	return (
		<Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
			<DialogTitle className="itBudgetDialogTitle">
				{invoice ? `${invoice.vendor || "Unknown vendor"} · ${invoice.invoice_number || fmtDay(invoice.invoice_date)}` : "Invoice"}
			</DialogTitle>
			<DialogContent className="itBudgetDialogContent">
				<Collapse in={Boolean(error)}>
					<Alert severity="error" onClose={() => setError("")}>
						{error}
					</Alert>
				</Collapse>
				{loading || !invoice ? (
					<div className="itBudgetEmpty">{loading ? <CircularProgress /> : null}</div>
				) : (
					<>
						<div className="itBudgetFormGrid">
							<Field label="Status">
								<span style={pillStyle(STATUS_PILLS[invoice.status] || STATUS_PILLS.New)}>{invoice.status}</span>
								{invoice.exclusion_reason ? <span className="itBudgetMuted"> {invoice.exclusion_reason}</span> : null}
							</Field>
							<Field label="Invoice date">{fmtDay(invoice.invoice_date)}</Field>
							<Field label="Received">{fmtDateTime(invoice.received)}</Field>
							<Field label="Mailbox / source">{invoice.mailbox || invoice.source}</Field>
							<Field label="Subtotal">{fmtUSD(invoice.subtotal_amount)}</Field>
							<Field label="Tax">{fmtUSD(invoice.tax_amount)}</Field>
							<Field label="Total">
								<b>{fmtUSD(invoice.total_amount)}</b>
							</Field>
							<Field label="Paid by">{invoice.card_label || invoice.payment_method}</Field>
							<Field label="Budget line">{invoice.budget_line || "Classified in Phase 3"}</Field>
							<Field label="Read by">{invoice.prompt_version || "—"}</Field>
							<Field label="Email subject">{invoice.email_subject}</Field>
							<Field label="File">
								<span className="itBudgetMono">{invoice.dropbox_path}</span>
							</Field>
						</div>

						{invoice.lines.length > 0 && (
							<div className="itBudgetTableContainer">
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>#</TableCell>
											<TableCell>Type</TableCell>
											<TableCell>Description</TableCell>
											<TableCell align="right">Qty / Hours</TableCell>
											<TableCell align="right">Amount</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{invoice.lines.map((l) => (
											<TableRow key={l.id}>
												<TableCell>{l.line_number}</TableCell>
												<TableCell>{l.line_type}</TableCell>
												<TableCell>{l.agreement ? `${l.agreement} · ` : ""}{l.description}</TableCell>
												<TableCell align="right">{l.hours ?? l.quantity ?? "—"}</TableCell>
												<TableCell align="right">{fmtUSD(l.amount)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}

						{invoice.notes && <div className="itBudgetMuted" style={{ whiteSpace: "pre-wrap" }}>{invoice.notes.slice(0, 1500)}</div>}

						{reread && (
							<div className="itBudgetCard">
								<div className="itBudgetOverline">
									Re-extract with {reread.prompt.name} v{reread.prompt.version} · nothing saved yet
								</div>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Field</TableCell>
											<TableCell>Stored</TableCell>
											<TableCell>New read</TableCell>
											<TableCell>Match</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{reread.diff.map((d) => (
											<TableRow key={d.field}>
												<TableCell className="itBudgetMono">{d.field}</TableCell>
												<TableCell>{String(d.stored ?? "—")}</TableCell>
												<TableCell>{String(d.extracted ?? "—")}</TableCell>
												<TableCell>{d.same ? <CheckCircleIcon fontSize="small" style={{ color: "#2e7d32" }} /> : <b style={{ color: "#b45309" }}>differs</b>}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
								{reread.review_reasons.length > 0 && <div className="itBudgetError">Would need review: {reread.review_reasons.join("; ")}</div>}
								<div className="itBudgetDialogButtons" style={{ marginTop: 10 }}>
									<Button variant="outlined" onClick={() => setReread(null)}>
										Discard
									</Button>
									<Button variant="contained" disabled={busy === "apply"} onClick={applyReExtract} startIcon={busy === "apply" ? <CircularProgress size={16} /> : null}>
										Save New Values
									</Button>
								</div>
							</div>
						)}

						{canEdit && (
							<>
								<TextField label="Notes" multiline minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
								{!excluded && (
									<TextField
										label="Reason to exclude"
										size="small"
										value={reason}
										onChange={(e) => setReason(e.target.value)}
										placeholder="e.g. $0 credit receipt, not a charge"
									/>
								)}
								<div className="itBudgetDialogButtons">
									<Button
										variant="outlined"
										startIcon={busy === "reextract" ? <CircularProgress size={16} /> : <CompareArrowsIcon />}
										disabled={Boolean(busy) || !invoice.dropbox_file_id}
										onClick={reExtract}
										style={{ marginRight: "auto" }}
									>
										Re-extract
									</Button>
									{excluded ? (
										<Button variant="outlined" disabled={Boolean(busy)} onClick={() => patch({ action: "restore" }, "Invoice restored.")}>
											Restore
										</Button>
									) : (
										<>
											<Button variant="outlined" color="error" disabled={Boolean(busy)} onClick={() => patch({ action: "duplicate" }, "Marked as a duplicate.")}>
												Mark Duplicate
											</Button>
											<Button variant="outlined" color="error" disabled={Boolean(busy) || !reason.trim()} onClick={() => patch({ action: "exclude", exclusion_reason: reason }, "Invoice excluded.")}>
												Exclude
											</Button>
										</>
									)}
									<Button variant="contained" disabled={Boolean(busy) || notes === (invoice.notes || "")} onClick={() => patch({ notes }, "Notes saved.")}>
										Save Notes
									</Button>
								</div>
							</>
						)}
					</>
				)}
				<div className="itBudgetDialogButtons">
					<Button onClick={onClose} disabled={Boolean(busy)}>
						Close
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default InvoiceDialog;
